<template>
    <v-container fluid class="flex-column">
        <h1 class="page-title page-title__primary">Параметры адаптации</h1>
        <p>Мы уже выбрали оптимальные параметры адаптации. Вы можете поменять их, при необходимости.</p>

        <v-alert color="success" dark v-if="isFinished">Расчет выполнен успешно</v-alert>
        <v-alert color="red" v-if="hasError">{{taskError}}</v-alert>

        <v-select v-model="optimizeParams.model" :items="models" label="Симулятор" outlined hide-details></v-select>
        <v-radio-group v-model="taskType">
            <div v-for="type in analysisTypes" :key="type.id">
                <v-radio :value="type.code" :label="type.title" :disabled="type.disabled"></v-radio>
                <div class="v-radio__description">{{type.description}}</div>
                <v-select v-if="type.code === 'predict' && taskType === 'predict'"
                    class="ml-8 my-4"
                    v-model="adaptTaskId"
                    :items="adaptTaskItems"
                    label="Результаты адаптации"
                    outlined hide-details
                ></v-select>
            </div>
        </v-radio-group>

        <v-expansion-panels>
            <v-expansion-panel :disabled="isFinished">
                <v-expansion-panel-header>Поменять параметры адаптации</v-expansion-panel-header>
                <v-expansion-panel-content>
                    <v-form>
                        <v-text-field v-model="optimizeParams.covariance" label="Ковариация" type="number" outlined></v-text-field>
                        <v-text-field v-model="optimizeParams.deviation" label="Отклонение" type="number" outlined></v-text-field>
                        <v-text-field v-model="optimizeParams.n_ensemble" label="Количество ансамблей" type="number" outlined></v-text-field>
                    </v-form>
                </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel :disabled="isFinished">
                <v-expansion-panel-header>
                    <div>
                        Поменять схему адаптации скважин
                        <v-chip class="ml-4" small v-if="adaptScheme && adaptScheme.length > 0">{{ adaptScheme.length || 0}}</v-chip>
                    </div>
                    <v-spacer></v-spacer>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                    <AdaptScheme :value="adaptScheme" @input="updateScheme" :problem="problem"></AdaptScheme>
                </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel :disabled="isFinished" v-if="needRelativePermCoeffs">
                <v-expansion-panel-header>Поменять стартовые коэффициенты для подбора ОФП</v-expansion-panel-header>
                <v-expansion-panel-content>
                    <v-form>
                        <v-text-field v-model="optimizeParams.a" label="a" type="number" outlined></v-text-field>
                        <v-text-field v-model="optimizeParams.no" label="no" type="number" outlined><template #label>n<sub>o</sub></template></v-text-field>
                        <v-text-field v-model="optimizeParams.nw" label="nw" type="number" outlined><template #label>n<sub>w</sub></template></v-text-field>
                    </v-form>
                </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel :disabled="isFinished">
                <v-expansion-panel-header>Поменять прочие параметры</v-expansion-panel-header>
                <v-expansion-panel-content>
                    <v-form>
                        <v-switch v-model="flags.mock" label="Тестовый прогон" hide-details></v-switch>
                        <v-switch v-model="flags.includeVr" label="Оптимизировать общий объем пласта" hide-details></v-switch>
                        <v-switch v-model="flags.includeRelativePerm" label="Оптимизировать ОФП" hide-details></v-switch>
                        <v-switch v-model="flags.connectionRelativePerm" label="Оптимизировать ОФП для каждого перетока" hide-details></v-switch>
                        <v-switch v-model="flags.matchConnectionFlow" label="Подгонять все итоговые объемы жидкостей под наблюдаемые значения (а не только нефть)" hide-details></v-switch>
                        <v-switch v-model="flags.logOptimizer" label="Журналировать оптимизатор" hide-details></v-switch>
                        <v-switch v-model="flags.logSimulator" label="Журналировать симулятор" hide-details></v-switch>
                        <v-switch v-model="flags.singleThread" label="Однопоточная оптимизация" hide-details></v-switch>
                    </v-form>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>
    </v-container>
</template>

<script>
import AdaptScheme from '../Task/AdaptScheme.vue';

const DEFAULT_RELATIVE_PERMEABILITY = {
    "a": 0.6,
    "no": 2,
    "nw": 2
}

export default {
    components: {AdaptScheme},
    props: {
        problem: {
            type: Object,
            default: null
        },
        modelValue: {
            type: Object,
            default: null
        }
    },
    data() {
        let taskType = 'adapt';
        let adaptTaskId = null;
        if (this.$route.query?.type) {
            taskType = this.$route.query.type;
        }

        if (this.$route.query?.adaptId) {
            adaptTaskId = this.$route.query.adaptId;
        }

        return {
            taskLoaded: false,
            taskType,
            ignoreUpdate: false,
            optimizeParams: {
                model: 'insim_fpt3d',
                covariance: 0.5,
                deviation: 2.0,
                n_ensemble: 200,
                a: DEFAULT_RELATIVE_PERMEABILITY.a,
                no: DEFAULT_RELATIVE_PERMEABILITY.no,
                nw: DEFAULT_RELATIVE_PERMEABILITY.nw
            },
            adaptTaskId,
            adaptTasks: [],
            adaptScheme: [],
            flags: {
                mock: false,
                includeVr: true,
                includeRelativePerm: true,
                connectionRelativePerm: false,
                matchConnectionFlow: true,
                logOptimizer: false,
                logSimulator: false,
                singleThread: false
            },
            flagIndex: {
                production: 0,
                debit: 1,
                bhp: 2,
                pressureDifference: 3
            },
            models: [
                {text: 'INSIM-FPT3D', value: 'insim_fpt3d'},
                {text: 'INSIM-FT', value: 'insim_ft'},
                {text: 'INSIM-BHP', value: 'insim_bhp'},
                {text: 'INSIM', value: 'insim'},
            ]
        }
    },
    async created() {
        this.emitInput();
        if (this.taskType === 'predict') {
            await this.loadAdaptTasks();
        }
    },
    watch: {
        task: {
            deep: true,
            handler() {
                if (this.ignoreUpdate) {
                    this.ignoreUpdate = false;
                    return;
                }

                if (this.task && this.task.params && this.taskLoaded === false) {
                    this.optimizeParams = this.task.params;
                    this.flags = this.task.flags;
                    this.adaptScheme = this.task.adaptScheme ? [...this.task.adaptScheme] : [];
                    this.taskLoaded = true;
                }
            }
        },
        problem: {
            deep: true,
            async handler() {
                this.emitInput();
                if (this.taskType === 'predict') {
                    await this.loadAdaptTasks();
                }
            }
        },
        flags: {
            deep: true,
            handler() {
                this.emitInput();
            }
        },
        optimizeParams: {
            deep: true,
            handler() {
                this.emitInput();
            }
        },
        async taskType(newType) {
            this.emitInput();
            if (newType === 'predict') {
                await this.loadAdaptTasks();
            }
        },
        isValid() {
            this.emitInput();
        }
    },
    methods: {
        flagsToOptions(flags) {
            let options = 0;
            if (flags.byVolume) {
                options += 1;
            }

            if (flags.byPermeability) {
                options += 2;
            }

            if (flags.everyConnection) {
                options += 4;
            }

            if (flags.byDebit) {
                options += 8;
            }

            return options;
        },
        optionsToFlags(options) {
            let flags = {
                byVolume: false,
                byPermeability: false,
                byDebit: false,
                everyConnection: false,
            }

            if (options >= 8) {
                flags.byDebit = true;
                options -= 8;
            }

            if (options >= 4) {
                flags.everyConnection = true;
                options -= 4;
            }

            if (options >= 2) {
                flags.byPermeability = true;
                options -= 2;
            }

            if (options >= 1) {
                flags.byVolume = true;
            }

            return flags;
        },
        replaceCharAtIndex(str, index, newChar) {
            return str.substring(0, index) + newChar + str.substring(index + 1)
        },
        getSchemaAdaptTemplate(schema) {
            let undefinedFlags = '    ';
            let optimizeFlags = '0000';

            for (let adaptKey of Object.keys(schema.adaptationParams)) {
                if (schema.adaptationParams[adaptKey]) {
                    let index = this.flagIndex[adaptKey];
                    optimizeFlags = this.replaceCharAtIndex(optimizeFlags, index, '1');
                }
            }

            //optimizeFlags = optimizeFlags.replace(/0*$/, '');
            let dataKeys = {
                production: 'oil_history_data',
                debit: 'schedule_data',
                bhp: 'bhp_data'
            };

            let historyData = {};
            for (let historyKey of Object.keys(schema.adaptationParams)) {
                let dataKey = dataKeys[historyKey];
                historyData[historyKey] = dataKey ? this.problem[dataKey] || {} : {};
            }

            let timeSteps = this.problem?.schedule_data?.t || [];
            let wells = this.problem?.wells || [];
            let adaptTemplate = {};

            for (let well of wells) {
                if (schema.selectedWells && schema.selectedWells.length > 0 && !schema.selectedWells.includes(well.id)) {
                    adaptTemplate[well.id] = Array(timeSteps.length).fill(undefinedFlags);
                    continue;
                }

                adaptTemplate[well.id] = [];
                for (let i = 0; i < timeSteps.length; i++) {
                    let t = timeSteps[i];
                    if ((schema.startDay && t < schema.startDay) || (schema.endDay && t > schema.endDay)) {
                        adaptTemplate[well.id].push(undefinedFlags);
                        continue;
                    }

                    let checkPassed = true;
                    for (let key of Object.keys(schema.filterParams)) {
                        let paramFilter = schema.filterParams[key];
                        let history = historyData[key]?.[well.id] || [];
                        let historyValue = typeof(history[i]) !== 'undefined' ? history[i] : null;
                        if (paramFilter.min !== '' && historyValue < paramFilter.min) {
                            checkPassed = false;
                            break;
                        }

                        if (paramFilter.max !== '' && historyValue > paramFilter.max) {
                            checkPassed = false;
                            break;
                        }
                    }

                    if (checkPassed) {
                        adaptTemplate[well.id].push(optimizeFlags);
                    }
                    else {
                        adaptTemplate[well.id].push(undefinedFlags);
                    }
                }
            }

            return adaptTemplate;
        },
        joinAdaptScheme(schema1, schema2) {
            let joinedSchema = {...schema1};

            for (let wellId in schema2) {
                if (joinedSchema[wellId]) {
                    let flags1 = joinedSchema[wellId];
                    let flags2 = schema2[wellId];
                    let joinedScheme = [];
                    for (let i = 0; i < flags1.length; i++) {
                        let flag1 = flags1[i];
                        let flag2 = flags2[i];

                        if (flag1 === '' || flag1 === ' ') {
                            joinedScheme.push(flag2);
                            continue;
                        }
                        else if (flag2 === '' || flag2 === ' ') {
                            joinedScheme.push(flag1);
                            continue;
                        }

                        let joinedFlag = flag1;
                        for (let j = 0; j < 4; j++) {
                            let char2 = flag2[j] || ' ';
                            if (char2 !== ' ') {
                                joinedFlag = this.replaceCharAtIndex(joinedFlag, j, char2);
                            }
                        }
                        joinedScheme.push(joinedFlag);
                    }
                    joinedSchema[wellId] = joinedScheme;
                }
                else {
                    joinedSchema[wellId] = schema2[wellId];
                }
            }

            return joinedSchema;
        },
        getAdaptTemplate() {
            if (!this.adaptScheme || !this.problem) {
                return null;
            }

            let template = null;
            let schemas = this.adaptScheme ? [...this.adaptScheme] : [];
            schemas = schemas.reverse();
            for (let schema of schemas) {
                let schemaTemplate = this.getSchemaAdaptTemplate(schema);
                if (template === null) {
                    template = schemaTemplate;
                }
                else {
                    template = this.joinAdaptScheme(template, schemaTemplate);
                }
            }

            if (!template) {
                return null;
            }

            let finalTemplateKeys = {
                production: 'oil',
                debit: 'debit',
                bhp: 'bhp',
                pressureDifference: 'dbhp'
            }

            let timeSteps = this.problem?.schedule_data?.t || [];
            let wells = this.problem?.wells || [];

            let undefinedFlags = '0000';
            let finalTemplate = {};
            for (let frontKey in finalTemplateKeys) {
                let backKey = finalTemplateKeys[frontKey];
                let allWellsAtStepData = [];
                for (let stepIndex in timeSteps) {
                    let allWellFlags = ''
                    for (let well of wells) {
                        let allWellTimesteps = template ? template[well.id] || [] : [];
                        let wellFlags = allWellTimesteps.length > stepIndex
                            ? allWellTimesteps[stepIndex] 
                            : undefinedFlags;
                        
                        let currentFlagIndex = this.flagIndex[frontKey];
                        let keyFlag = wellFlags[currentFlagIndex] || '0';
                        if (keyFlag === ' ') {
                            keyFlag = '0';
                        }
                        allWellFlags += keyFlag;
                    }

                    allWellsAtStepData.push(allWellFlags.replace(/0*$/, ''));
                }

                finalTemplate[backKey] = allWellsAtStepData;
            }

            return finalTemplate;
        },
        updateScheme(newScheme) {
            if (JSON.stringify(this.adaptScheme) === JSON.stringify(newScheme)) {
                return;
            }

            this.adaptScheme = [...newScheme];
            this.emitInput();
        },
        emitInput() {
            if (!this.isValid) {
                this.$emit('input', null);
            }

            let task = this.task ? {...this.task} : {};
            task = {
                ...task,
                problemId: this.problem?.id,
                type: this.taskType || 'adapt',
                params: {
                    ...task.params,
                    ...this.optimizeParams,
                    covariance: parseFloat(task.params?.covariance),
                    deviation: parseFloat(task.params?.deviation),
                    n_ensemble: parseInt(task.params?.n_ensemble),
                },
                options: {...this.options},
                flags: {...task.flags, ...this.flags},
                adaptScheme: this.adaptScheme ? [...this.adaptScheme] : null,
                adaptTemplate: this.getAdaptTemplate(),
            }

            if (this.taskType === 'predict') {
                task['optimize_id'] = this.adaptTaskId;
            }

            this.ignoreUpdate = true;
            this.$emit('input', task);
        },
        async loadAdaptTasks() {
            if (!this.problem) {
                return;
            }

            this.adaptTasks = await this.$store.dispatch('task/loadItems', {
                problemId: this.problem?.id,
                type: 'adapt',
                status: 'finished'
            });
        }
    },
    computed: {
        options() {
            return this.flagsToOptions(this.flags);
        },
        isFinished() {
            return this.task && this.task?.status === 'finished';
        },
        hasError() {
            return Boolean(this.taskError);
        },
        taskError() {
            return this.$store.state.task.lastError;
        },
        needRelativePermCoeffs() {
            //return ['insim_bhp', 'insim_fpt3d'].includes(this.optimizeParams.model);
            return true;
        },
        analysisTypes() {
            return this.$store.state.problem.analysisTypes;
        },
        adaptTaskItems() {
            return this.adaptTasks?.map(task => ({value: task.id, text: `[${task.id}] ${task?.updated || '-'}`}));
        },
        isValid() {
            if (this.taskType === 'predict' && !this.adaptTaskId) {
                return false;
            }

            return true;
        }
    }
}
</script>

<style scoped>

</style>